import { useCurrentDate } from "@sellar-io/components";
import {
  FulfillmentTypeListResponseType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import {
  findLatestPreOrderAvailableFromDate,
  getNextStartDate,
} from "@sellar-io/common";
import { useEstimateDirectDeliveryDate } from "../../hooks/useEstimateDirectDeliveryDate";
import { DeliveryOrderItemType } from "./CourierDeliveryOptionsCard";
import { DeliveryOptionsCardCompact } from "./DeliveryOptionsCardCompact";

export const DirectDeliveryOptionsCard = ({
  deliveryArea,
  supplier,
  directFulfillmentType,
  orderItems,
}: {
  deliveryArea?: SupplierStorefrontV2GetResponseType["DeliveryArea"];
  supplier: SupplierGetResponseType;
  directFulfillmentType:
    | Omit<
        FulfillmentTypeListResponseType[number],
        | "id"
        | "name"
        | "OrderRules"
        | "numberOfCustomers"
        | "numberOfZones"
        | "DeliveryMethodContainerRules"
      >
    | undefined;
  orderItems?: DeliveryOrderItemType;
}) => {
  return (
    <DeliveryOptionsCardCompact
      supplierId={supplier.id}
      description={
        deliveryArea ? (
          <DirectDeliveryContentCompact
            deliveryArea={deliveryArea}
            supplier={supplier}
            orderItems={orderItems}
          />
        ) : null
      }
      fulfillmentType={directFulfillmentType}
      title={
        <p>
          Delivered by <strong>{supplier.Business.name}</strong>
        </p>
      }
    />
  );
};

const DirectDeliveryContentCompact = ({
  deliveryArea,
  supplier,
  orderItems,
}: {
  deliveryArea: SupplierStorefrontV2GetResponseType["DeliveryArea"];
  supplier: SupplierGetResponseType;
  orderItems?: DeliveryOrderItemType;
}) => {
  const currentDate = useCurrentDate();
  const latestAvailablePreOrderFromDate = findLatestPreOrderAvailableFromDate(
    orderItems?.map((item) => ({
      availableFrom: item.Product.availableFrom,
    })) ?? [],
  );
  const outOfOfficeReturnDate = supplier.SupplierOutOfOffice?.outOfOffice
    ? supplier.SupplierOutOfOffice.returnDate
    : null;

  const startDateReference = getNextStartDate({
    latestAvailablePreOrderFromDate,
    outOfOfficeReturnDate,
    courierDeliveryScheduleStartDate: null,
    currentDate,
  });

  const { orderByDate, nextDeliveryDate } = useEstimateDirectDeliveryDate({
    deliveryArea,
    startDateReference,
  });
  if (!nextDeliveryDate) return null;

  const message = "Get it by";

  return (
    <div className={"flex flex-col"}>
      <p>
        {message} <strong>{nextDeliveryDate.format("ddd D MMM")}</strong>
      </p>
      {orderByDate ? (
        <p>
          Order by{" "}
          <span className={"underline"}>{orderByDate.format("HH:mm")}</span> on{" "}
          <span className={"underline"}>{orderByDate.format("ddd D MMM")}</span>
        </p>
      ) : null}
    </div>
  );
};
